import {useCallback, useState} from 'react';
import styles from './FormResetPassword.module.scss'
import {Controller, SubmitHandler, useForm} from 'react-hook-form';
import {Box, FormHelperText, IconButton, Input, InputAdornment, TextField} from "@mui/material";
import {Visibility, VisibilityOff} from "@mui/icons-material";
import {toast} from "react-hot-toast";
import {yupResolver} from "@hookform/resolvers/yup";
import * as yup from "yup";
import useAuth from "../../../../hooks/auth/useAuth";
import {useTranslation} from "../../../../hooks/translations/useTranslation";
import {useNavigate} from "react-router";
import {api} from "../../../../services";
import DefaultButton from "../../ui/default-button/DefaultButton";
import {useParams} from "react-router-dom";

export interface IFormInputs {
  password: string;
  submit: string;
}

const FormResetPassword = () => {

    const {isAuthenticated} = useAuth()
    const t = useTranslation();

    const {id} = useParams();


    const navigate = useNavigate();

    const schema = yup.object({
      password: yup.string()
        .matches(/^(?=.*[A-Z])(?=.*[!@#$&*])(?=.*[0-9])(?=.*[a-z]).{8,30}$/,
          "Password must have between 8-30 characters, with lowercase, uppercase, number and special character")
        .required(t('register.required-password')),
    }).required();

    const {
      handleSubmit,
      control,
      formState: {errors},
    } = useForm<IFormInputs>({resolver: yupResolver(schema)});

    const submit: SubmitHandler<IFormInputs> = async (data) => {
      await api.auth.confirmResetPassword(id || '', {
        password: data.password,
      }).then(() => {
        toast.success(t("register.success-reset-password"), {id: 'policies'});
        navigate('/login');
      }).catch((error) => {
        if (error.errors?.password) control.setError("password", {
          type: 'custom',
          message: error.errors.password
        }, {shouldFocus: true})

        toast.error(error?.message);

      });

    }

    const [showPassword, setShowPassword] = useState(false);

    const onToggleShowPassword = useCallback(() => {
      showPassword ? setShowPassword(false) : setShowPassword(true);
    }, [showPassword]);


    if (isAuthenticated) {
      navigate("/home")
    }

    return (
      <div className={styles['form-register']}>
        <form noValidate onSubmit={handleSubmit(submit)}>
          <Controller
            name="password"
            control={control}
            defaultValue=""
            render={({field, fieldState: {error}}) => (
              <TextField
                {...field}
                fullWidth
                error={!!error}
                helperText={error?.message}
                label={t('register.password-label')}
                margin="normal"
                variant="outlined"
                autoComplete="off"
                type={showPassword ? 'text' : 'password'}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={onToggleShowPassword}
                      >
                        {showPassword ? <Visibility/> : <VisibilityOff/>}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              >
                <Input/>
              </TextField>
            )}
          />

          {errors.submit && (
            <Box sx={{mt: 3}}>
              <FormHelperText error>{errors.submit.message}</FormHelperText>
            </Box>
          )}
          <Box sx={{mt: 2}}>
            <DefaultButton type="submit" color='primary' className={styles['form-register__button']}>
              Confirm
            </DefaultButton>
          </Box>
        </form>
      </div>
    );
  }
;

export default FormResetPassword;
